// Typed operations (decision D41-C4, PRD stage 4): the four server actions an agent may ask for, and the grant kinds an
// organization administrator may approve for each one. The agent names only the action and its arguments; the application,
// the Coolify project, the environment and the server always come from the project's single deployment target.
import { z } from "zod";

export const SERVICE_GRANT_KINDS = ["once", "mission", "persistent"] as const;
export type ServiceGrantKind = (typeof SERVICE_GRANT_KINDS)[number];

/** The option keys of a grant request: "1" once, "2" during the task, "3" persistent, "4" deny. */
export const GRANT_OPTIONS = { "1": "once", "2": "mission", "3": "persistent", "4": "deny" } as const;
export type GrantOptionKey = keyof typeof GRANT_OPTIONS;

export const TYPED_OPERATION_TYPES = ["service.restart", "service.redeploy", "database.provision", "env.set_secret_ref"] as const;
export type TypedOperationType = (typeof TYPED_OPERATION_TYPES)[number];

/** Commit SHA as the agent may name it: 40 (SHA-1) or 64 (SHA-256) lowercase hex digits. */
export const COMMIT_SHA_RE = /^(?:[0-9a-f]{40}|[0-9a-f]{64})$/;
/** Environment variable name, as the agent may name it. */
export const ENV_KEY_RE = /^[A-Z_][A-Z0-9_]{0,63}$/;
/** PostgreSQL database name to create. */
export const DATABASE_NAME_RE = /^[a-z][a-z0-9_]{0,62}$/;
/** Identifier token of a Coolify resource (application, project, environment): no spaces, no shell characters. */
export const COOLIFY_TOKEN_RE = /^[A-Za-z0-9_-]{1,64}$/;
/** UUID in lowercase canonical form (the vault secret id). */
export const UUID_TEXT_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** What the agent may request. Targets are not arguments: they come from the project's deployment target. */
export const typedOperationSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("service.restart") }).strict(),
  z.object({ type: z.literal("service.redeploy"), commit_sha: z.string().regex(COMMIT_SHA_RE) }).strict(),
  z.object({ type: z.literal("database.provision"), engine: z.literal("postgresql"), name: z.string().regex(DATABASE_NAME_RE) }).strict(),
  z.object({ type: z.literal("env.set_secret_ref"), key: z.string().regex(ENV_KEY_RE), secret_id: z.string().regex(UUID_TEXT_RE) }).strict(),
]);
export type TypedOperation = z.infer<typeof typedOperationSchema>;

export type TypedOperationKind = "coolify_restart" | "coolify_redeploy" | "coolify_database_create" | "coolify_env_set";
export type GrantEffectKey = "restart" | "redeploy" | "database_create" | "env_set";
export type GrantReversalKey = "restart" | "redeploy_previous" | "database_manual_delete" | "env_restore_previous";

/** Per operation: the server operation kind it runs as, the grant kinds that may approve it, and the texts keys. */
export const TYPED_OPERATIONS: Record<
  TypedOperationType,
  { kind: TypedOperationKind; grantKinds: readonly ServiceGrantKind[]; effectKey: GrantEffectKey; reversalKey: GrantReversalKey }
> = {
  "service.restart": { kind: "coolify_restart", grantKinds: ["once", "mission", "persistent"], effectKey: "restart", reversalKey: "restart" },
  "service.redeploy": { kind: "coolify_redeploy", grantKinds: ["once", "mission"], effectKey: "redeploy", reversalKey: "redeploy_previous" },
  "database.provision": {
    kind: "coolify_database_create",
    grantKinds: ["once", "mission"],
    effectKey: "database_create",
    reversalKey: "database_manual_delete",
  },
  "env.set_secret_ref": { kind: "coolify_env_set", grantKinds: ["once", "mission"], effectKey: "env_set", reversalKey: "env_restore_previous" },
};
